import {
  EngravingOptionType,
  OPTIONS,
  type OptionType,
  SIZES,
  type SizeType,
  STYLES,
  type StyleType,
} from '@/components/EngravingModal/types';
import type { MetadataItem } from '@/graphql/saleor/api';
import { getMetaValue } from '@/lib/metadata/helpers';
import type { Maybe } from '@/lib/types';

export interface EngravingDetails {
  size: SizeType;
  style: StyleType;
  text: string;
  type?: OptionType;
}

const MIN_TEXT_LENGTH = 2;
const MAX_TEXT_LENGTH = 10;
export const MAX_MESSAGE_LENGTH_LARGE = 10;
export const MAX_MESSAGE_LENGTH_MEDIUM = 12;

export type MessageEngravingValidationError =
  | 'empty'
  | 'invalidCharacters'
  | 'cursiveAmpersandNotSupported'
  | 'largeTooLong'
  | 'mediumTooLong';

const MESSAGE_ALLOWED_PATTERN = /^[A-Za-z0-9& ]+$/;
const MESSAGE_INVALID_CHARACTERS_PATTERN = /[^A-Za-z0-9& ]/g;

const toTrimmed = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const isAllowedInitialsText = (text?: string): boolean => {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (trimmed.length < MIN_TEXT_LENGTH) return false;
  if (trimmed.length > MAX_TEXT_LENGTH) return false;
  return /^(?!\.)(?!.*\.\.)(?!.*\.$)[A-Za-z0-9.]+$/.test(trimmed);
};

const isAllowedDateText = (text?: string): boolean => {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (trimmed.length < MIN_TEXT_LENGTH) return false;
  if (trimmed.length > MAX_TEXT_LENGTH) return false;
  return /^[0-9.]+$/.test(trimmed);
};

export const sanitizeMessageEngravingValue = (value: string): string =>
  value.replaceAll(MESSAGE_INVALID_CHARACTERS_PATTERN, '');

export const getMessageEngravingPreviewValue = (
  value: string,
  size: SizeType,
): string => {
  const maxLength =
    size === 'L' ? MAX_MESSAGE_LENGTH_LARGE : MAX_MESSAGE_LENGTH_MEDIUM;
  return sanitizeMessageEngravingValue(value).slice(0, maxLength);
};

export const validateMessageEngraving = (
  text: string,
  size: SizeType,
  style: StyleType = 'Block',
): MessageEngravingValidationError | null => {
  if (!text) return 'empty';
  if (style === 'Cursive' && text.includes('&'))
    return 'cursiveAmpersandNotSupported';
  if (!MESSAGE_ALLOWED_PATTERN.test(text)) return 'invalidCharacters';
  if (size === 'L' && text.length > MAX_MESSAGE_LENGTH_LARGE)
    return 'largeTooLong';
  if (size === 'M' && text.length > MAX_MESSAGE_LENGTH_MEDIUM)
    return 'mediumTooLong';
  return null;
};

const isAllowedMessageTextForParse = (
  text: string,
  size: SizeType,
): boolean => {
  if (!text) return false;
  if (!MESSAGE_ALLOWED_PATTERN.test(text)) return false;
  if (size === 'L' && text.length > MAX_MESSAGE_LENGTH_LARGE) return false;
  if (size === 'M' && text.length > MAX_MESSAGE_LENGTH_MEDIUM) return false;
  return true;
};

const isAllowedTextForType = (
  text: string,
  type: OptionType,
  size: SizeType,
): boolean => {
  if (type === EngravingOptionType.Message)
    return isAllowedMessageTextForParse(text, size);
  if (type === EngravingOptionType.Date) return isAllowedDateText(text);
  return isAllowedInitialsText(text);
};

const isAllowedSize = (v?: string): v is SizeType =>
  SIZES.includes(v as unknown as SizeType);

const isAllowedStyle = (v?: string): v is StyleType =>
  STYLES.includes(v as unknown as StyleType);

const isAllowedType = (v?: string): v is OptionType =>
  (OPTIONS as readonly OptionType[]).includes(v as OptionType);

export const parseEngravingDetails = (
  value: Maybe<string>,
): EngravingDetails | null => {
  if (typeof value !== 'string') return null;
  const trimmedValue = value.trim();
  if (trimmedValue === '') return null;

  try {
    const parsed = JSON.parse(trimmedValue) as Record<string, unknown>;
    const text = toTrimmed(parsed.text);
    const size = toTrimmed(parsed.size);
    const style = toTrimmed(parsed.style);
    const type = toTrimmed(parsed.type);

    if (
      !text ||
      !size ||
      !style ||
      !type ||
      !isAllowedSize(size) ||
      !isAllowedStyle(style) ||
      !isAllowedType(type) ||
      !isAllowedTextForType(text, type, size)
    )
      return null;

    const details: EngravingDetails = {
      size,
      style,
      text,
      type,
    };

    return details;
  } catch {
    return null;
  }
};

export const getEngravingDetailsFromMetadata = (
  metadata: readonly MetadataItem[] | undefined,
): EngravingDetails | null => {
  const raw = metadata
    ? getMetaValue(metadata, 'item.engraving_details')
    : undefined;
  return parseEngravingDetails(raw);
};
